import ClientCache from "@/utils/login/ClientCache";
import KeyboardKey from "@/utils/constants/KeyboardKey";
import Translator from "@/utils/common/Translator.ts";
import {onMounted, onUnmounted} from "vue";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";
import {useLoadingStore} from "@/stores/LoadingStore.ts";
import FieldInfoUtils from "@/utils/common/FieldInfoUtils.ts";
import {Dialog} from "quasar";
import ServerLogDialog from "@/components/dialogs/ServerLogDialog.vue";
import {useErrorsStore} from "@/stores/ToastDataStore.ts";
import {useLoginInfoStore} from "@/stores/loginInfo.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import router from "@/router";
import RouteNames from "@/router/RouteNames.ts";
import PlaceTokens from "@/utils/constants/PlaceTokens.ts";
import ViewMode from "@/utils/constants/ViewMode.ts";
import type {RouteLocationRaw} from "vue-router";
import GlobalShortcutTargetType from "@/utils/constants/GlobalShortcutTargetType.ts";

const GLOBAL_SHORTCUT_PREFIX = "global_";

export default class ShortCutsUtil {
  static registeredShortcuts = new Map<string, string[]>();
  static reversedShortcuts = new Map<string, namaTypes.DTOCMNKeyboardShortcut>();
  static globalShortcutRoutes = new Map<string, RouteLocationRaw | string>();

  static registerShortcutKeys(shortCuts: namaTypes.DTOShortCutsDefinition | null | undefined) {
    let templates = ClientCache.getTemplates();
    if (templates) {
      for (let templateMetadata of templates) {
        this.registerShortcut_(templateMetadata.shortcut, templateMetadata.template!.id);
      }
    }
    if (shortCuts) {
      if (shortCuts.details) {
        for (let line of shortCuts.details) {
          this.registerShortCut(line.shortCut);
        }
      }
      if (shortCuts.globalShortcuts) {
        for (let line of shortCuts.globalShortcuts) {
          const route = this.buildRouteForGlobalShortcut(line);
          if (!route)
            continue;
          const key = GLOBAL_SHORTCUT_PREFIX + line.g2Id;
          this.globalShortcutRoutes.set(key, route);
          this.registerShortcut_(line.shortCut, key);
        }
      }
    } else {
      this.registeredShortcuts.clear();
      this.reversedShortcuts.clear();
      this.globalShortcutRoutes.clear();
    }
  }

  static buildRouteForGlobalShortcut(line: namaTypes.DTOShortCutsDefinitionGlobalLine): RouteLocationRaw | string | null {
    switch (line.type) {
      case GlobalShortcutTargetType.EntityList:
        if (!line.targetEntity)
          return null;
        return {
          name: RouteNames.LIST_VIEW_ROUTE,
          query: {[PlaceTokens.PRM_ENTITY_TYPE]: line.targetEntity}
        };
      case GlobalShortcutTargetType.NewRecord:
        if (!line.targetEntity)
          return null;
        return {
          name: RouteNames.EDIT_VIEW_ROUTE,
          query: {
            [PlaceTokens.PRM_ENTITY_TYPE]: line.targetEntity,
            [PlaceTokens.PRM_VIEW]: ViewMode.NEW + '',
            ...(line.defaultsTemplate?.id ? {[PlaceTokens.PRM_EDIT_TEMPLATE_ID]: line.defaultsTemplate.id} : {})
          }
        };
      case GlobalShortcutTargetType.Record:
        if (!line.targetRecord?.id)
          return null;
        return {
          name: RouteNames.EDIT_VIEW_ROUTE,
          query: {
            [PlaceTokens.PRM_ENTITY_TYPE]: line.targetRecord.entityType,
            [PlaceTokens.PRM_ID]: line.targetRecord.id,
            [PlaceTokens.PRM_VIEW]: ViewMode.EDIT + '',
          }
        };
      case GlobalShortcutTargetType.Link:
        return line.link || null;
      case GlobalShortcutTargetType.InternalLink:
        let link = line.link;
        if (!link)
          return null;
        if (link.startsWith("#"))
          link = link.substring(1);
        if (link.startsWith("/"))
          link = link.substring(1);
        return {path: link};
      case GlobalShortcutTargetType.GlobalSearch:
        return GlobalShortcutTargetType.GlobalSearch;
      case GlobalShortcutTargetType.MenuSearch:
        return GlobalShortcutTargetType.MenuSearch;
      default:
        return null;
    }
  }

  static handleGlobalShortcut(businessFunctions: string[]): boolean {
    for (const fn of businessFunctions) {
      if (!fn.startsWith(GLOBAL_SHORTCUT_PREFIX))
        continue;
      const route = this.globalShortcutRoutes.get(fn);
      if (!route)
        continue;
      if (typeof route === 'string') {
        if (route === GlobalShortcutTargetType.GlobalSearch) {
          setTimeout(() => {
            const input = document.querySelector('#header-search-container input') as HTMLInputElement;
            if (input)
              input.focus();
          });
        } else if (route === GlobalShortcutTargetType.MenuSearch) {
          useNamaFlagsStore().isSideBarOpen = true;
          setTimeout(() => {
            document.getElementById('sideBarSearchInput')?.focus();
          }, 300);
        } else {
          window.open(route, '_blank');
        }
      } else {
        router.push(route);
      }
      return true;
    }
    return false;
  }

  static registerShortCut(shortCut: namaTypes.DTOShortCut | undefined) {
    if (!shortCut)
      return null;
    let keyboardShortcut = shortCut.shortCut;
    let id = shortCut.bizFunction;
    return this.registerShortcut_(keyboardShortcut, id);
  }

  static registerShortcut_(keyboardShortcut: namaTypes.DTOCMNKeyboardShortcut | undefined, id: string | undefined) {
    if (!keyboardShortcut || !keyboardShortcut.theKey)
      return null;
    let eventKey = this.shortcutToEventKey(keyboardShortcut);
    let val: string[] = this.registeredShortcuts.get(eventKey)!;
    if (!val)
      this.registeredShortcuts.set(eventKey, val = []);
    val.push(id!);
    this.reversedShortcuts.set(id!, keyboardShortcut);
    return eventKey;
  }

  static unregisterShortcut_(keyboardShortcut: namaTypes.DTOCMNKeyboardShortcut | undefined, id: string) {
    if (!keyboardShortcut || !keyboardShortcut.theKey)
      return null;
    let eventKey = this.shortcutToEventKey(keyboardShortcut);
    let val: string[] | undefined = this.registeredShortcuts.get(eventKey)!;
    if (val && val.indexOf(id) >= 0)
      val.splice(val.indexOf(id), 1);
    this.reversedShortcuts.delete(id!);
    return eventKey;
  }

  static shortcutToEventKey(keyboardShortcut: namaTypes.DTOCMNKeyboardShortcut): string {
    let key = keyboardShortcut.theKey!;
    let ctrl = keyboardShortcut.useCtrl;
    let shift = keyboardShortcut.useShift;
    let alt = keyboardShortcut.useAlt;
    return this.eventKey(KeyboardKey[key], alt, ctrl, shift);
  }

  static eventKey(key: string, alt: boolean | undefined, ctrl: boolean | undefined, shift: boolean | undefined) {
    return key.toUpperCase() + "_" + this.bToI(alt) + this.bToI(ctrl) + this.bToI(shift);
  }

  static bToI(b: boolean | undefined) {
    return b === true ? "1" : "0";
  }

  static getFunctionShortCut(_function: string) {
    return this.reversedShortcuts.get(_function);
  }

  static describe(shortCut: namaTypes.DTOKeyBoardShortCut) {
    if (!shortCut)
      return "";
    let builder = " [";
    if (shortCut.useAlt)
      builder += "Alt + ";
    if (shortCut.useCtrl)
      builder += "Ctrl + ";
    if (shortCut.useShift)
      builder += "Shift + ";
    if (shortCut.theKey!.length > 2)
      builder += Translator.translateId(shortCut.theKey);
    else
      builder += shortCut.theKey;
    builder += "]";
    return builder;
  }

  static addShortCutToTitle(title: string, functionId: string) {
    let functionShortCut = this.getFunctionShortCut(functionId);
    if (functionShortCut && functionShortCut)
      return title + " " + this.describe(functionShortCut);
    return title;
  }
}

export async function handleKeyDownAndCallShortcutIfNeeded(e: KeyboardEvent, handler?: (businessFunctions: string[]) => Promise<boolean>) {
  const namaFlagsStore = useNamaFlagsStore();
  if (!e.key)
    return;
  let key = e.key.toUpperCase();
  if (key == "F7")
    e.preventDefault();
  if (e.code.startsWith("Key"))
    key = e.code.substring(3);
  if (key == 'X' && e.ctrlKey && e.altKey && e.shiftKey) {
    namaFlagsStore.toggleShowIds();
    e.preventDefault();
  } else if (key == 'X' && e.ctrlKey && e.altKey) {
    // todo :: dev mode
    namaFlagsStore.activateReplicationOptions();
    e.preventDefault();
  } else if (key == 'L' && e.ctrlKey && e.altKey) {
    namaFlagsStore.activateServerLog();
    if (!useErrorsStore().serverLogDialogVisible) {
      Dialog.create({
        component: ServerLogDialog,
      });
    }
    e.preventDefault();
  } else if (key == 'I' && e.ctrlKey && e.altKey) {
    namaFlagsStore.activateShowFieldInfo();
    e.preventDefault();
  } else if (key == 'H' && e.ctrlKey && e.altKey) {
    namaFlagsStore.activateShowHelpMessages();
    e.preventDefault();
  } else if (key == 'T' && e.ctrlKey && e.altKey) {
    namaFlagsStore.activateEditUIElementProperties();
    e.preventDefault();
  } else if (key == 'F9' && FieldWidgetUtil.focusedField) {
    if (FieldWidgetUtil.focusedField.handlesTooltips()) {
      FieldWidgetUtil.focusedField.displayTooltip();
    } else if (FieldWidgetUtil.focusedField.presenter.fetchDispatcher()) {
      if (FieldWidgetUtil.focusedField.isGridCell())
        return;
      await FieldWidgetUtil.focusedField.presenter.fetchDispatcher().displayTooltip(FieldWidgetUtil.focusedField.fieldId(), FieldWidgetUtil.focusedField.fieldId(), -1, false);
    }
    e.preventDefault();
  }
  let eventKey = ShortCutsUtil.eventKey(key, e.altKey, e.ctrlKey, e.shiftKey);
  let businessFunctions = ShortCutsUtil.registeredShortcuts.get(eventKey);
  if (!businessFunctions)
    return;
  if (ShortCutsUtil.handleGlobalShortcut(businessFunctions)) {
    e.preventDefault();
    return;
  }
  if (handler) {
    e.preventDefault();
    await FieldWidgetUtil.saveEditingFieldsAndGrids();
    await handler(businessFunctions);
  }
}

export function useGlobalShortcuts(handler?: (businessFunctions: string[]) => Promise<boolean>) {
  const onShortcutKeyDown = async (e: KeyboardEvent) => {
    let loading = useLoadingStore();
    if (loading.isLoadingActive || loading.timerRunning)
      return;
    await handleKeyDownAndCallShortcutIfNeeded(e, handler);
  };
  const onContextMenuHandler = async (e: MouseEvent) => {
    if (!useNamaFlagsStore().showFieldInfoActive)
      return;
    await FieldInfoUtils.showFieldInfoHandler(e)
  }
  onMounted(() => {
    window.addEventListener("contextmenu", onContextMenuHandler)
    window.addEventListener("keydown", onShortcutKeyDown)
  })

  onUnmounted(() => {
    window.removeEventListener("contextmenu", onContextMenuHandler)
    window.removeEventListener("keydown", onShortcutKeyDown);
  })
}

export function enableQrLoginDialogKeyboardShortcut() {
  const onShortcutKeydown = async (e: KeyboardEvent) => {
    let key = e.key;
    if (!key)
      return;
    if (key.startsWith("Key"))
      key = key.substring(3)
    if (e.altKey && key == "F12" && !useLoginInfoStore().qrCodeDialogVisible)
      await LoginUtils.showLoginByQR();
  }
  onMounted(() => {
    window.addEventListener("keydown", onShortcutKeydown)
  })
  onUnmounted(() => {
    window.removeEventListener("keydown", onShortcutKeydown)
  })
}
